import * as React from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineFire } from 'react-icons/ai';
import Card from 'components/Card';
import SectionTitle from 'components/SectionTitle';
import { TickerAggregatedDetails } from 'components/OptionAggregatedTable/props';
import { themeDark } from 'styles/theme';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import BarChartTopTickers from './BarChartTopTickers';
import { TopTickerProps } from './props';
import { FeedTypeToggle } from './styles';

const MAX_GRAPHS = 12;

export const DEFAULT_TOP_TICKER_GRAPH_INDEX = 0;

type GraphMetricFamily = 'positioning' | 'premium' | 'contracts' | 'unusual' | 'sweeps';
type NetDirection = 'bullish' | 'bearish';

type GraphOption = {
  name: string;
  family: GraphMetricFamily;
  color: string;
  sortKey: keyof TickerAggregatedDetails;
  netSortKey?: keyof TickerAggregatedDetails;
  label: string;
  netLabel?: string;
  prefix: string;
  postfix: string;
  formatter: (num: number) => string | number;
};

type GraphOptionGroup = {
  key: string;
  label: string;
  options: GraphOption[];
};

const FAMILY_LABELS: Record<GraphMetricFamily, string> = {
  positioning: 'Positioning',
  premium: 'Premium',
  contracts: 'Contracts',
  unusual: 'Unusual',
  sweeps: 'Sweeps',
};

const NET_DIRECTION_LABELS: Record<NetDirection, string> = {
  bullish: 'Bullish',
  bearish: 'Bearish',
};

// In net mode the bars are colored by sign (bullish vs bearish). Each metric
// family keeps its own identity color pair so the unusual and sweep graphs read
// the same way they do in the sector sentiment graphs, instead of the generic
// green/red used for total flow.
const NET_SIGNED_COLORS: Record<GraphMetricFamily, { bullish: string; bearish: string }> = {
  positioning: { bullish: themeDark.color.graphCall, bearish: themeDark.color.graphPut },
  premium: { bullish: themeDark.color.graphCall, bearish: themeDark.color.graphPut },
  contracts: { bullish: themeDark.color.graphCall, bearish: themeDark.color.graphPut },
  unusual: { bullish: themeDark.color.graphUnusualCall, bearish: themeDark.color.graphUnusualPut },
  sweeps: { bullish: themeDark.color.graphSweep, bearish: themeDark.color.graphSweepPut },
};

const options: GraphOption[] = [
  {
    name: 'OTM',
    family: 'positioning',
    color: themeDark.color.graphCall,
    sortKey: 'otm',
    label: 'OTM %',
    prefix: '',
    postfix: '%',
    formatter: (num: number) => Math.round(num),
  },
  {
    name: 'Unusual OTM',
    family: 'unusual',
    color: themeDark.color.graphUnusualCall,
    sortKey: 'unusualOtm',
    label: 'OTM %',
    prefix: '',
    postfix: '%',
    formatter: (num: number) => Math.round(num),
  },
  {
    name: 'Momentum',
    family: 'positioning',
    color: themeDark.color.graphMomentum,
    sortKey: 'momentum', // CHANGE ONCE YOU HAVE CALCULATION
    label: 'Multiple of Daily Average',
    prefix: '',
    postfix: 'x',
    formatter: (num: number) => Math.round(num),
  },
  {
    name: 'Unusual Volume',
    family: 'unusual',
    color: themeDark.color.graphUnusualCall,
    sortKey: 'unusualContracts',
    netSortKey: 'netUnusualContracts',
    label: 'Unusual Contracts',
    netLabel: 'Net Unusual Contracts',
    prefix: '',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Unusual Premium',
    family: 'unusual',
    color: themeDark.color.graphUnusualCall,
    sortKey: 'unusualPremium',
    netSortKey: 'netUnusualPremium',
    label: 'Unusual Premium',
    netLabel: 'Net Unusual Premium',
    prefix: '$',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Call Volume',
    family: 'contracts',
    color: themeDark.color.graphCall,
    sortKey: 'callVolume',
    label: 'Call Contracts',
    prefix: '',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Call Premium',
    family: 'premium',
    color: themeDark.color.graphCall,
    sortKey: 'callPremium',
    label: 'Call Premium',
    prefix: '$',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Put Volume',
    family: 'contracts',
    color: themeDark.color.graphPut,
    sortKey: 'putVolume',
    label: 'Put Contracts',
    prefix: '',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Put Premium',
    family: 'premium',
    color: themeDark.color.graphPut,
    sortKey: 'putPremium',
    label: 'Put Premium',
    prefix: '$',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Sweep Volume',
    family: 'sweeps',
    color: themeDark.color.graphSweep,
    sortKey: 'sweepContracts',
    netSortKey: 'netSweepContracts',
    label: 'Sweep Contracts',
    netLabel: 'Net Sweep Contracts',
    prefix: '',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Sweep Premium',
    family: 'sweeps',
    color: themeDark.color.graphSweep,
    sortKey: 'sweepPremium',
    netSortKey: 'netSweepPremium',
    label: 'Sweep Premium',
    netLabel: 'Net Sweep Premium',
    prefix: '$',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Total Contracts',
    family: 'contracts',
    color: themeDark.color.graphCall,
    sortKey: 'contracts',
    netSortKey: 'netContracts',
    label: 'Total Contracts',
    netLabel: 'Net Contracts',
    prefix: '',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
  {
    name: 'Total Premium',
    family: 'premium',
    color: themeDark.color.graphCall,
    sortKey: 'premium',
    netSortKey: 'netPremium',
    label: 'Total Premium',
    netLabel: 'Net Premium',
    prefix: '$',
    postfix: '',
    formatter: (num: number) => abbreviateNumber(num),
  },
];

const DEFAULT_NET_TOP_TICKER_GRAPH_INDEX = Math.max(
  options.findIndex((option) => option.name === 'Total Premium'),
  DEFAULT_TOP_TICKER_GRAPH_INDEX,
);
const GRAPH_FAMILIES = Object.keys(FAMILY_LABELS) as GraphMetricFamily[];
const NET_DIRECTIONS = Object.keys(NET_DIRECTION_LABELS) as NetDirection[];
const NET_GRAPH_OPTION_GROUPS = [
  { key: 'total', label: 'Total', optionNames: ['Total Premium', 'Total Contracts'] },
  { key: 'unusual', label: 'Unusual', optionNames: ['Unusual Premium', 'Unusual Volume'] },
  { key: 'sweeps', label: 'Sweeps', optionNames: ['Sweep Premium', 'Sweep Volume'] },
];

const isGraphOptionAvailable = (option: GraphOption, metricMode: 'total' | 'net') =>
  metricMode === 'total' || !!option.netSortKey;

const getGraphOptionsByName = (optionNames: string[]) =>
  optionNames
    .map((optionName) => options.find((option) => option.name === optionName))
    .filter((option): option is GraphOption => !!option);

const getGraphOptionGroups = (metricMode: 'total' | 'net'): GraphOptionGroup[] => {
  if (metricMode === 'net') {
    return NET_GRAPH_OPTION_GROUPS.map((group) => ({
      key: group.key,
      label: group.label,
      options: getGraphOptionsByName(group.optionNames).filter((option) =>
        isGraphOptionAvailable(option, metricMode),
      ),
    })).filter((group) => group.options.length > 0);
  }

  return GRAPH_FAMILIES.map((family) => ({
    key: family,
    label: FAMILY_LABELS[family],
    options: options.filter(
      (option) => option.family === family && isGraphOptionAvailable(option, metricMode),
    ),
  })).filter((group) => group.options.length > 0);
};

const getDisplayGraphOption = (
  option: GraphOption,
  metricMode: 'total' | 'net',
  netDirection: NetDirection,
) => {
  if (metricMode !== 'net' || !option.netSortKey) {
    return option;
  }

  const baseLabel = option.netLabel || option.label;
  const directionLabel = NET_DIRECTION_LABELS[netDirection];

  return {
    ...option,
    name: `${directionLabel} ${baseLabel}`,
    sortKey: option.netSortKey,
    label: `${directionLabel} ${baseLabel}`,
  };
};

const getMetricSelectGraphName = (option: GraphOption, metricMode: 'total' | 'net') =>
  metricMode === 'net' && option.netLabel ? option.netLabel : option.name;

const getControlGraphName = (option: GraphOption, metricMode: 'total' | 'net') => {
  const name = metricMode === 'net' && option.netLabel ? option.netLabel : option.name;

  if (metricMode === 'net') {
    return name
      .replace(/^Net /, '')
      .replace(/^Unusual /, '')
      .replace(/^Sweep /, '');
  }

  if (option.family === 'premium') {
    return name.replace(' Premium', '');
  }

  if (option.family === 'contracts') {
    return name.replace(' Volume', '').replace(' Contracts', '');
  }

  if (option.family === 'unusual') {
    return name.replace(/^Net /, '').replace(/^Unusual /, '');
  }

  if (option.family === 'sweeps') {
    return name.replace(/^Net /, '').replace(/^Sweep /, '');
  }

  return name;
};

const getNumericGraphValue = (
  ticker: TickerAggregatedDetails,
  sortKey: keyof TickerAggregatedDetails,
) => Number(ticker[sortKey] || 0);

const sortTopTrades = (
  trades: TickerAggregatedDetails[],
  sortKey: keyof TickerAggregatedDetails,
  metricMode: 'total' | 'net',
  netDirection: NetDirection,
) =>
  [...trades]
    .sort((a, b) => {
      const first = getNumericGraphValue(a, sortKey);
      const last = getNumericGraphValue(b, sortKey);

      if (metricMode === 'net' && netDirection === 'bearish') {
        return first - last;
      }

      return last - first;
    })
    .slice(0, MAX_GRAPHS);

const MetricSelectButton: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean;
    onClick: () => void;
  }>
> = ({ children, isOpen, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex h-[22px] w-[104px] items-center rounded-sm border px-2 text-[10px] font-medium leading-none transition-colors sm:w-[124px] ${
      isOpen
        ? 'border-button-color bg-button-color text-white'
        : 'border-palette-border bg-palette-black-1 text-gray-200 hover:border-button-color hover:text-white'
    }`}
  >
    <span className="min-w-0 flex-1 truncate">{children}</span>
    <span className="ml-1 text-[8px]">▾</span>
  </button>
);

const OptionFlowTable: React.FC<React.PropsWithChildren<TopTickerProps>> = ({
  tradesByTicker,
  presetGraph,
  metricMode = 'total',
}) => {
  const [activeGraph, setActiveGraph] = React.useState(DEFAULT_TOP_TICKER_GRAPH_INDEX);
  const [netDirection, setNetDirection] = React.useState<NetDirection>('bullish');
  const [isMetricMenuOpen, setIsMetricMenuOpen] = React.useState(false);
  const [metricMenuPosition, setMetricMenuPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const metricWidgetRef = React.useRef<HTMLDivElement>(null);
  const metricButtonRef = React.useRef<HTMLDivElement>(null);
  const metricMenuRef = React.useRef<HTMLDivElement>(null);
  const rawActiveGraph = Math.min(activeGraph, options.length - 1);
  const normalizedActiveGraph =
    metricMode === 'net' && !options[rawActiveGraph]?.netSortKey
      ? DEFAULT_NET_TOP_TICKER_GRAPH_INDEX
      : rawActiveGraph;
  const activeBaseGraphOption = options[normalizedActiveGraph];
  const activeGraphOption = getDisplayGraphOption(activeBaseGraphOption, metricMode, netDirection);
  const showNetDirectionToggle = metricMode === 'net';
  const sortKey = activeGraphOption.sortKey;
  const topTrades = sortTopTrades(tradesByTicker, sortKey, metricMode, netDirection);
  const graphOptionGroups = getGraphOptionGroups(metricMode);

  const updateMetricMenuPosition = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const triggerRect = metricButtonRef.current?.getBoundingClientRect();
    if (!triggerRect) {
      return;
    }

    const widgetRect = metricWidgetRef.current?.getBoundingClientRect();
    const boundaryLeft = Math.max(16, (widgetRect?.left ?? 16) + 8);
    const boundaryRight = Math.min(
      window.innerWidth - 16,
      (widgetRect?.right ?? window.innerWidth - 16) - 8,
    );
    const boundaryWidth = Math.max(160, boundaryRight - boundaryLeft);
    const width = Math.min(560, boundaryWidth);
    const preferredLeft = triggerRect.left + triggerRect.width / 2 - width / 2;
    const left = Math.max(boundaryLeft, Math.min(preferredLeft, boundaryRight - width));
    const spaceBelow = window.innerHeight - triggerRect.bottom - 16;
    const spaceAbove = triggerRect.top - 16;
    const shouldOpenAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(360, Math.max(120, shouldOpenAbove ? spaceAbove : spaceBelow));
    const top = shouldOpenAbove
      ? Math.max(16, triggerRect.top - maxHeight - 6)
      : triggerRect.bottom + 6;

    setMetricMenuPosition({
      top,
      left,
      width,
      maxHeight,
    });
  }, []);

  React.useEffect(() => {
    if (!isMetricMenuOpen) {
      return;
    }

    updateMetricMenuPosition();

    window.addEventListener('resize', updateMetricMenuPosition);
    window.addEventListener('scroll', updateMetricMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMetricMenuPosition);
      window.removeEventListener('scroll', updateMetricMenuPosition, true);
    };
  }, [isMetricMenuOpen, updateMetricMenuPosition]);

  React.useEffect(() => {
    if (!isMetricMenuOpen) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (metricButtonRef.current?.contains(target) || metricMenuRef.current?.contains(target)) {
        return;
      }

      setIsMetricMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMetricMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMetricMenuOpen]);

  React.useEffect(() => {
    setActiveGraph(presetGraph);
  }, [presetGraph]);

  React.useEffect(() => {
    if (metricMode === 'net' && !options[activeGraph]?.netSortKey) {
      setActiveGraph(DEFAULT_NET_TOP_TICKER_GRAPH_INDEX);
    }
  }, [activeGraph, metricMode]);

  const metricMenu =
    isMetricMenuOpen && metricMenuPosition && typeof document !== 'undefined'
      ? (createPortal(
          <div
            ref={metricMenuRef}
            className="fixed rounded-sm border border-palette-border bg-palette-black-2 p-2 shadow-2xl"
            style={{
              top: metricMenuPosition.top,
              left: metricMenuPosition.left,
              width: metricMenuPosition.width,
              maxHeight: metricMenuPosition.maxHeight,
              overflowY: 'auto',
              zIndex: 9999,
            }}
          >
            <div
              className="grid gap-2"
              style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))' }}
            >
              {graphOptionGroups.map((group) => (
                <div key={group.key}>
                  <div className="mb-1 text-[9px] font-semibold uppercase leading-none text-gray-500">
                    {group.label}
                  </div>
                  <div className="space-y-1">
                    {group.options.map((option) => {
                      const optionIndex = options.indexOf(option);
                      const isActive = normalizedActiveGraph === optionIndex;

                      return (
                        <button
                          key={option.name}
                          type="button"
                          onClick={() => {
                            setActiveGraph(optionIndex);
                            setIsMetricMenuOpen(false);
                          }}
                          className={`block h-[24px] w-full rounded-sm border px-2 text-left text-[10px] font-medium leading-none transition-colors ${
                            isActive
                              ? 'border-button-color bg-button-color text-white'
                              : 'border-palette-border bg-palette-black-1 text-gray-300 hover:border-button-color hover:text-white'
                          }`}
                        >
                          {getControlGraphName(option, metricMode)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        ) as unknown as React.ReactNode)
      : null;

  return (
    <div ref={metricWidgetRef} className="h-full w-full">
      <Card className="flex h-full w-full flex-col" isDark>
        <SectionTitle
          Icon={AiOutlineFire}
          title="Options Top Tickers"
          className="mb-1 shrink-0 flex-wrap gap-y-1"
          right={
            <div className="relative ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-1">
              {showNetDirectionToggle && (
                <div className="flex shrink-0 items-center">
                  {NET_DIRECTIONS.map((direction) => (
                    <FeedTypeToggle
                      key={direction}
                      isActive={netDirection === direction}
                      onClick={() => setNetDirection(direction)}
                    >
                      {NET_DIRECTION_LABELS[direction]}
                    </FeedTypeToggle>
                  ))}
                </div>
              )}
              <div ref={metricButtonRef}>
                <MetricSelectButton
                  isOpen={isMetricMenuOpen}
                  onClick={() => setIsMetricMenuOpen((isOpen) => !isOpen)}
                >
                  {getMetricSelectGraphName(activeBaseGraphOption, metricMode)}
                </MetricSelectButton>
              </div>
            </div>
          }
        />
        {metricMenu}
        {tradesByTicker.length === 0 ? null : (
          <div className="min-h-0 w-full max-w-full flex-1">
            {options.map((f, i) => {
              const displayOption = getDisplayGraphOption(f, metricMode, netDirection);
              return (
                <React.Fragment key={f.name}>
                  {normalizedActiveGraph === i && (
                    <BarChartTopTickers
                      topTrades={topTrades}
                      color={displayOption.color}
                      label={displayOption.label}
                      prefix={displayOption.prefix}
                      postfix={displayOption.postfix}
                      sortKey={displayOption.sortKey}
                      formatter={displayOption.formatter}
                      useSignedColors={metricMode === 'net' && !!f.netSortKey}
                      bullishColor={NET_SIGNED_COLORS[f.family].bullish}
                      bearishColor={NET_SIGNED_COLORS[f.family].bearish}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default OptionFlowTable;
